import React from 'react';

import {Checkbox} from '@optimaxdev/design-system';
import {ActionCreatorWithPayload} from '@reduxjs/toolkit';
import classNames from 'classnames';
import {useFormContext} from 'react-hook-form';
import {connect} from 'react-redux';

import {useFeature} from 'components/feature';
import {ApplicationStoreType} from 'desktop/store';
import {AddressFormItemType, AddressFormType, CurrentAddressType} from 'desktop/types/addressType';

import {CustomerAddressesType} from 'desktop/types/customerAddressesType';
import {useIsOneStepAvailable} from 'hooks/useIsOneStepAvailable/useIsOneStepAvailable';

import {saveBillingAddress as saveBillingAddressAction} from '../../store/reducers/deliveryStep/billingAddress/billingAddress';
import {startSetAddressesSamenessSaga} from '../../store/reducers/deliveryStep/billingAndShippingIsSame/billingAndShippingIsSame';
import {startUpdateCustomerAddressSaga} from '../../store/reducers/deliveryStep/customerAddresses/customerAddresses';
import {
    getAddressesSameness,
    getCustomerAddresses,
} from '../../store/selectors/deliveryStep/deliveryStep';

import s from './deliveryFormCheckbox.scss';

export type PropsType = {
    isSameAddress: boolean;
    setSameAddress: (isSameAddress: boolean) => void;
    updateCustomerAddress: ActionCreatorWithPayload<{id: string; type: CurrentAddressType | null}>;
    customerAddresses: CustomerAddressesType;
    saveBillingAddress: typeof saveBillingAddressAction;
};

/**
 * Delivery form checkbox for checkout
 *
 * @constructor
 */
export const DeliveryFormCheckbox: React.FC<PropsType> = ({
    customerAddresses,
    isSameAddress,
    saveBillingAddress,
    setSameAddress,
    updateCustomerAddress,
}) => {
    const isFacelift = useFeature('dCheckoutFacelift');

    const {
        formState: {errors},
        getValues,
        setValue,
    } = useFormContext<AddressFormType>();

    const {isOneStepAvailable} = useIsOneStepAvailable();

    const handleChange = async () => {
        if (!isSameAddress) {
            const values = getValues();
            Object.entries(values.shippingAddress).forEach(([key, value]) => {
                const error = errors.shippingAddress?.[key as keyof AddressFormItemType];
                saveBillingAddress({
                    [key]: {
                        error: Boolean(error?.message || ''),
                        success: !error,
                        value,
                    },
                });
                setValue(`billingAddress.${key as keyof AddressFormItemType}`, value, {
                    shouldValidate: false,
                });
            });
        }

        setSameAddress(!isSameAddress);

        if (
            customerAddresses.shipping_address_id !== customerAddresses.billing_address_id &&
            !isSameAddress
        ) {
            const shippingAddressId = customerAddresses.shipping_address_id || '';
            updateCustomerAddress({id: String(shippingAddressId), type: 'billing'});
        }
    };

    return (
        <div
            className={classNames(s.wrapper, {
                [s.facelift]: isFacelift,
                [s.oneStep]: isOneStepAvailable,
            })}
        >
            <Checkbox
                isChecked={isSameAddress}
                onChange={handleChange}
                size="small"
                value="My Billing & Shipping addresses are the same"
            >
                <p className={s.title}>My Billing & Shipping addresses are the same</p>
            </Checkbox>
        </div>
    );
};

/**
 * Map state to props
 * @param {ApplicationStoreType} store - redux store
 * @returns {PropsType} props
 */
const mapStateToProps = (store: ApplicationStoreType) => ({
    customerAddresses: getCustomerAddresses(store),
    isSameAddress: getAddressesSameness(store),
});

const mapDispatchToProps = {
    saveBillingAddress: saveBillingAddressAction,
    setSameAddress: startSetAddressesSamenessSaga,
    updateCustomerAddress: startUpdateCustomerAddressSaga,
};

export const DeliveryFormCheckboxConnected = connect(
    mapStateToProps,
    mapDispatchToProps,
)(DeliveryFormCheckbox);
